import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import UpgradeModal from "@/components/modals/UpgradeModal";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import CanvasPage from "@/pages/CanvasPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminPage from "@/pages/AdminPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import GuestJoinPage from "@/pages/GuestJoinPage";
import CollabCanvasPage from "@/pages/CollabCanvasPage";

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="spinner" />
    </div>
  );
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const HomeRedirect = () => {
  const { token, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="spinner" />
    </div>
  );
  if (token) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
};

/**
 * AppRouter detects OAuth callback (session_id in hash) SYNCHRONOUSLY during render.
 * This prevents race conditions where ProtectedRoute might run before OAuth is processed.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback SYNCHRONOUSLY during render (not in useEffect)
  // This ensures we process the session_id BEFORE any protected route checks
  // Check both location.hash and window.location.hash to handle all cases
  const hash = location.hash || window.location.hash;
  if (hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/canvas" element={<ProtectedRoute><CanvasPage /></ProtectedRoute>} />
        <Route path="/checkout-success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        {/* Collaboration routes - more specific route first */}
        <Route path="/collab/canvas/:mapId" element={<CollabCanvasPage />} />
        <Route path="/collab/:shareToken" element={<GuestJoinPage />} />
        {/* Admin routes - accessible only via URL */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      <UpgradeModal />
      <Toaster position="bottom-right" />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
